import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import getPlanProbillsColumns, { PLAN_PROBILLS_COLUMN_OPTIONS, PLAN_PROBILLS_COLUMNS_HIDDEN_BY_DEFAULT } from "./planProbillsColumns";
import PlanProbillsHeader from "./planProbillsHeader/planProbillsHeader";
import { resetSelectedProbillNumbers, setSelectedProbillNumbers, setSelectedTerminal } from "../../../slices/dispatch-planning/dispatchSelectedProbillsSlice";
import "./planProbills.scss";
import { PlannableProbills } from "../../../app/data/plan-probills/models";
import { dispatchPlanProbillsSelector, getPlannableProbills } from "../../../slices/dispatch-planning/dispatchPlanProbillsSlice";
import { DispatchPlanProbillsState } from "../../../slices/dispatch-planning/dispatchProbillsState";
import UserState from "../../../slices/user/UserState";
import { userSelector } from "../../../slices/user/userSlice";
import { DataTable } from "../../../ui-components/data-table";
import { PAGE_IDS } from "../../../app/data/common/pageIds";
import { CreateAppointment } from "../../appointment/createAppointment";

const PlanProbills: React.FC<{}> = memo((props) => {
  const dispatch = useDispatch();
  const [terminal, setTerminal] = useState<number | null | undefined>();
  const [appointmentProbillNumber, setAppointmentProbillNumber] = useState<number | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hiddenSet = new Set(PLAN_PROBILLS_COLUMNS_HIDDEN_BY_DEFAULT);
    return PLAN_PROBILLS_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hiddenSet.has(id) }),
      {} as Record<string, boolean>
    );
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    PLAN_PROBILLS_COLUMN_OPTIONS.map(({ id }) => id)
  );

  const onColumnVisibilityChange = useCallback(
    (updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
      setColumnVisibility((prev) => (typeof updater === "function" ? updater(prev) : updater));
    },
    []
  );

  const dispatchPlanProbillsState: DispatchPlanProbillsState = useSelector(
    dispatchPlanProbillsSelector
  );
  const userState: UserState = useSelector(
    userSelector
  );
  useEffect(() => {
    const userTerminal = userState.activeTerminal?.id;
    onTerminalChanged(userTerminal);
    // eslint-disable-next-line
  }, [userState.activeTerminal])

  useEffect(() => {
    return () => {
      dispatch(resetSelectedProbillNumbers());
    }
  }, [dispatch])

  const selectedProbillsChanged = React.useCallback(
    (state: {
      selectedRowsData: PlannableProbills[];
    }) => {
      const selectedProbill: PlannableProbills[] = state.selectedRowsData.map(
        (selectedRow) => selectedRow
      );
      dispatch(setSelectedProbillNumbers(selectedProbill));
    },
    [dispatch]
  );

  const onTerminalChanged = (userTerminal: number | null | undefined) => {
    setTerminal(userTerminal);
    dispatch(resetSelectedProbillNumbers());
    if (!userTerminal) return; // can not get the Probills without knowing the terminal
    dispatch(setSelectedTerminal(userTerminal));
    dispatch(getPlannableProbills(`${userTerminal}`));
  };

  const onScheduleAppointment = useCallback((probillNumber: number) => {
    setAppointmentProbillNumber(probillNumber);
  }, []);

  const columns = useMemo(
    () => getPlanProbillsColumns({
      onScheduleAppointment,
      terminalNumber: terminal ?? undefined,
    }),
    [onScheduleAppointment, terminal]
  );

  return (
    <ContentContainer
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.PLAN_ROUTE}
          title="Plan Route"
        >
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-plan-probills">
        {!terminal && (
          <div className="xgs-plan-probills__error-card">
            <div className="xgs-plan-probills__error-card__icon" aria-hidden>
              !
            </div>
            <div className="xgs-plan-probills__error-card__content">
              <h3 className="xgs-plan-probills__error-card__title">No terminal assigned</h3>
              <p className="xgs-plan-probills__error-card__message">
                You do not have assigned terminals. Email{" "}
                <a className="xgs-plan-probills__error-card__link" href="mailto:helpdesk@xgsi.com">
                  helpdesk@xgsi.com
                </a>{" "}
                to request terminal assignment for your account.
              </p>
            </div>
          </div>
        )}
        {terminal && (
          <>
            <DataTable
              isLoading={dispatchPlanProbillsState.loading}
              columns={columns}
              data={dispatchPlanProbillsState.plannableProbills ?? []}
              rowHeight={52}
              ignoreCellClick="selection"
              cursorPointer={true}
              onSelectedRowsChange={selectedProbillsChanged}
              responsive={true}
              sorting={true}
              enableMultiSort={true}
              filters={true}
              autoResetSelectedRows={true}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={onColumnVisibilityChange}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              showColumnSelector={true}
              columnSelectorOptions={PLAN_PROBILLS_COLUMN_OPTIONS}
              enableColumnResize={true}
              enableColumnReorder={true}
              stickyHeaders={true}
              noResultsText="No records to display"
              enableRowSelection={true}
              rowClickTogglesSelection={true}
              keepTableOnLoading={true}
            />
            <PlanProbillsHeader />
          </>
        )}
      </div>
      {appointmentProbillNumber !== null && (
        <CreateAppointment
          probillNumber={appointmentProbillNumber}
          onClose={() => setAppointmentProbillNumber(null)}
        />
      )}
    </ContentContainer>
  );
});

export default PlanProbills;
